import React, {useMemo} from 'react';
import type {List} from 'immutable';

import {Badge, Text, WithTooltip} from '@albert-io/atomic';
import EnrolledStudentsActionsDropdown from 'client/Classrooms/Classroom/Teacher/ManageRoster/EnrolledStudents/EnrolledStudentsActionsDropdown/EnrolledStudentsActionsDropdown.react';

import {InviteMethodCards} from './InviteMethodCards';
import {PeopleEmptyState} from './PeopleEmptyState';
import {RosterTable} from './RosterTable';
import type {RosterColumn} from './RosterTable';
import {useClientSort} from './useClientSort';
import type {RosterClassroom, RosterInvitation, RosterStudent} from './usePeopleRoster';

// Stable across renders so the sort doesn't recompute needlessly.
const getSortValue = (student: RosterStudent, key: string): string => {
  if (key === 'first') {
    return student.getFirstName();
  }
  if (key === 'email') {
    return student.getEmail();
  }
  return student.getLastName();
};

interface Props {
  classroom: RosterClassroom;
  students: List<RosterStudent>;
  invitations: List<RosterInvitation>;
  search: string;
  onShowModal: (modal: string) => void;
}

/**
 * Enrolled students. The whole roster loads up front (see usePeopleRoster), so this is a plain
 * semantic table with in-memory search/sort — no MandarkTable and no table library. Row actions and
 * the getting-started invite cards are the reused legacy flows.
 */
export const EnrolledList = ({classroom, students, invitations, search, onShowModal}: Props) => {
  const filtered = useMemo(() => {
    const all = students.toArray();
    const terms = search.toLowerCase().split(' ').filter(Boolean);
    if (!terms.length) {
      return all;
    }
    return all.filter((student) => {
      const haystack =
        `${student.getFirstName()} ${student.getLastName()} ${student.getEmail()}`.toLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
  }, [students, search]);

  const {sorted, sortKey, sortDir, toggleSort} = useClientSort(filtered, getSortValue, 'last');

  const hasManagedStudents = useMemo(
    () => students.toArray().some((student) => student.getMeta().isManagedUser()),
    [students]
  );

  // Whole-roster empty states (parity with the legacy EnrolledStudents view).
  if (students.isEmpty()) {
    if (invitations.isEmpty()) {
      return <InviteMethodCards onShowModal={onShowModal} />;
    }
    return (
      <PeopleEmptyState
        body='Not all students have accepted their invitations. To re-send invitations, view the Invited section.'
        icon='envelope-circle-user'
        title='No enrolled students'
      />
    );
  }

  // getSchool() can be null; degrade the copy rather than crash.
  const schoolName = classroom.getSchool()?.getName() ?? 'your school';

  const managedColumn: RosterColumn<RosterStudent> = {
    key: 'managed',
    headerClassName: 'w-x6',
    cellClassName: 'px-x2 py-x1-half text-center',
    render: (student) =>
      student.getMeta().isManagedUser() ? (
        <WithTooltip content={`Managed by ${schoolName}`} focusable>
          <Badge emphasis='bold' icon={['far', 'school']} iconAlign='center' />
        </WithTooltip>
      ) : null
  };

  const columns: RosterColumn<RosterStudent>[] = [
    {
      key: 'last',
      label: 'Last Name',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half',
      render: (student) => <Text>{student.getLastName()}</Text>
    },
    {
      key: 'first',
      label: 'First Name',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half',
      render: (student) => <Text>{student.getFirstName()}</Text>
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half break-all',
      render: (student) => <Text>{student.getEmail()}</Text>
    },
    ...(hasManagedStudents ? [managedColumn] : []),
    {
      key: 'actions',
      headerClassName: 'w-x6',
      srOnlyHeader: 'Actions',
      cellClassName: 'px-x2 py-x1-half text-right',
      render: (student) => (
        <EnrolledStudentsActionsDropdown classroom={classroom} student={student} />
      )
    }
  ];

  return (
    <RosterTable
      ariaLabel='Enrolled students'
      columns={columns}
      getRowKey={(student) => student.getId()}
      noResultsMessage='No students match your search.'
      onSort={toggleSort}
      rows={sorted}
      sortDir={sortDir}
      sortKey={sortKey}
    />
  );
};
